<template>
  <div class="flex justify-center md:py-12">
    <div class="w-full">
      <div class="mb-8 text-center md:mb-12">
        <h3 class="mb-5">
          {{ $t("ourClientsReviews") }}
        </h3>
        <UiButton
          class="whitespace-nowrap"
          color="secondary"
          @click="addReviewDialog = true"
        >
          {{ $t("leaveReview") }}
        </UiButton>

        <template v-if="hasReviews">
          <div class="mt-6 mb-1 text-lg tracking-wider">
            {{ $t("productRating") }}
          </div>
          <div class="mb-2 flex flex-wrap items-center justify-center gap-2">
            <UiRating :rating="Number(product.reviews_avg_rating)" class="-ml-0.5 flex" />
            <span class="text-sm">
              {{ Number(product.reviews_avg_rating).toFixed(1) }}
              ({{ product.reviews_count }} {{ $t("reviews") }})
            </span>
          </div>

          <div v-if="headerMediaItems.length" class="mt-5 flex flex-wrap justify-center gap-2.5">
            <button
              v-for="(item, idx) in headerMediaItems"
              :key="idx"
              class="h-[60px] w-[60px] min-[460px]:h-[90px] min-[460px]:w-[90px] shrink-0 overflow-hidden rounded-sm"
              @click="openDrawer(item.review)"
            >
              <UiLazyImg
                :src="item.url"
                :alt="item.review.author"
                class="h-full w-full object-cover"
                cover
              />
            </button>
          </div>
        </template>
      </div>

      <div v-if="hasReviews" class="relative">
        <Swiper
          :slides-per-view="1"
          :breakpoints="{ 390: { slidesPerView: 'auto' } }"
          :space-between="16"
          :allow-touch-move="true"
          simulate-touch
          class="reviews-carousel w-full"
          @swiper="onSwiper"
          @slide-change="onSlideChange"
          @transition-end="onSlideChange"
        >
          <SwiperSlide
            v-for="review in reviews"
            :key="review.id"
            class="min-[390px]:!w-[350px]"
          >
            <div class="flex h-full flex-col border border-gray-strokes p-5">
              <div class="mb-3 text-center">
                <div class="font-medium break-words">{{ review.author }}</div>
                <UiRating
                  :rating="Number(review.stars)"
                  class="-ml-0.5 mt-1 flex"
                />
              </div>

              <p v-if="review?.currentTranslation?.title" class="mb-3 font-medium leading-5 break-words">
                {{ review.currentTranslation.title }}
              </p>

              <div class="flex flex-auto gap-3">
                <div class="min-w-0 flex-auto">
                  <div
                    v-if="review?.currentTranslation?.description"
                    :ref="(el) => setTextRef(el as Element | null, review.id)"
                    class="line-clamp-3 text-sm [&_p]:text-sm font-normal leading-5 break-words"
                    v-html="review.currentTranslation.description"
                  />
                  <button
                    v-if="truncatedIds[review.id]"
                    class="mt-1 text-sm underline hover:no-underline"
                    @click="openDrawer(review)"
                  >
                    {{ $t("readMore") }}
                  </button>
                </div>
                <button
                  v-if="getFirstMediaUrl(review)"
                  class="h-[65px] w-[65px] shrink-0 overflow-hidden"
                  @click="openDrawer(review)"
                >
                  <UiLazyImg
                    :src="getFirstMediaUrl(review)!"
                    :alt="review.author"
                    class="h-full w-full object-cover"
                    cover
                  />
                </button>
              </div>

              <p
                v-if="(review as any).recommend"
                class="mt-3 text-sm italic"
              >
                {{ $t("yesIWouldRecommendToAFriend") }}
              </p>
            </div>
          </SwiperSlide>
        </Swiper>

        <template v-if="swiperInstance">
          <button
            v-if="!isSwiperBeginning"
            class="absolute left-[20px] top-1/2 z-10 hidden sm:flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-md"
            @click="swiperInstance.slidePrev()"
          >
            <UiIcon name="chevron_left" class="h-5 w-5" />
          </button>
          <button
            v-if="!isSwiperEnd"
            class="absolute right-[20px] top-1/2 z-10 hidden sm:flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-md"
            @click="swiperInstance.slideNext()"
          >
            <UiIcon name="chevron_right" class="h-5 w-5" />
          </button>
        </template>
      </div>

      <!-- <PagesProductEmptyReviews @addReview="addReviewDialog = true" /> -->

      <DialogsReviewDetailDrawer v-model="drawerOpen" :review="selectedReview" />

      <DialogsWriteReviewDialog
        v-model="addReviewDialog"
        :product="product"
        @success="successDialog = true"
      />
      <DialogsReviewSuccess v-model="successDialog" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { Swiper, SwiperSlide } from "swiper/vue";
import type { Swiper as SwiperType } from "swiper";
import { ProductData, ReviewData } from "ecom-types";

const props = defineProps<{ product: ProductData }>();

const addReviewDialog = ref(false);
const successDialog = ref(false);
const drawerOpen = ref(false);
const selectedReview = ref<ReviewData | null>(null);

const swiperInstance = ref<SwiperType | null>(null);
const isSwiperBeginning = ref(true);
const isSwiperEnd = ref(false);
const truncatedIds = reactive<Record<number, boolean>>({});

function setTextRef(el: Element | null, id: number) {
  if (!el) return;
  nextTick(() => {
    truncatedIds[id] = el.scrollHeight > el.clientHeight;
  });
}

const reviews = computed<ReviewData[]>(
  () => [...(((props.product as any).reviews as ReviewData[]) || [])].reverse(),
);

const hasReviews = computed(() => reviews.value.length > 0);

const headerMediaItems = computed(() => {
  const items: Array<{ url: string; review: ReviewData }> = [];
  for (const review of reviews.value) {
    if (!review.medias?.length) continue;
    for (const media of review.medias) {
      if (items.length >= 4) return items;
      const url = (media as any)?.file?.url || (media as any)?.url;
      if (url) items.push({ url, review });
    }
  }
  return items;
});

function updateNavState(swiper: SwiperType) {
  isSwiperBeginning.value = swiper.isBeginning;
  isSwiperEnd.value = swiper.translate <= swiper.maxTranslate() + 1;
}

function onSwiper(swiper: SwiperType) {
  swiperInstance.value = swiper;
  updateNavState(swiper);
}

function onSlideChange(swiper: SwiperType) {
  updateNavState(swiper);
}

function getFirstMediaUrl(review: ReviewData): string | null {
  const media = review.medias?.[0];
  if (!media) return null;
  return (media as any)?.file?.url || (media as any)?.url || null;
}


function openDrawer(review: ReviewData) {
  selectedReview.value = review;
  drawerOpen.value = true;
}
</script>

<style>
.reviews-carousel .swiper-wrapper {
  align-items: stretch;
}
.reviews-carousel .swiper-slide {
  height: auto;
}
</style>
